/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Parses URL search params into a filter state object.
 * Array values are stored as comma-separated strings in the URL.
 */
const parseFiltersFromUrl = (searchParams) => {
    const filters = {};

    const status = searchParams.get('status');
    if (status) filters.status = status;

    const search = searchParams.get('search');
    if (search) filters.search = search;

    const treatment = searchParams.get('treatment');
    if (treatment) filters.treatment = treatment.split(',');

    const assignee = searchParams.get('assignee');
    if (assignee) filters.assignee = assignee.split(',');

    const tags = searchParams.get('tags');
    if (tags) filters.tags = tags.split(',');

    const referral = searchParams.get('referral');
    if (referral) filters.referral = referral;

    const startDate = searchParams.get('startDate');
    if (startDate) filters.startDate = startDate;

    const endDate = searchParams.get('endDate');
    if (endDate) filters.endDate = endDate;

    const depositPaid = searchParams.get('depositPaid');
    if (depositPaid) filters.depositPaid = depositPaid;

    const archiveReason = searchParams.get('archiveReason');
    if (archiveReason) filters.archiveReason = archiveReason;

    const bookedWith = searchParams.get('bookedWith');
    if (bookedWith) filters.bookedWith = bookedWith.split(',');

    const consultationBookedWith = searchParams.get('consultationBookedWith');
    if (consultationBookedWith) filters.consultationBookedWith = consultationBookedWith.split(',');

    const noTreatment = searchParams.get('noTreatment');
    if (noTreatment === 'true') filters.noTreatment = true;

    return filters;
};

/**
 * Serializes a filter state object into URL search params.
 * Omits empty/default values to keep URLs clean.
 */
const serializeFiltersToUrl = (filterState) => {
    const params = new URLSearchParams();

    if (filterState.status) params.set('status', filterState.status);
    if (filterState.search) params.set('search', filterState.search);

    if (filterState.treatment?.length) {
        const ids = filterState.treatment.map((t) => (typeof t === 'object' ? t.id : t));
        params.set('treatment', ids.join(','));
    }

    if (filterState.assignee?.length) {
        const ids = filterState.assignee.map((a) => (typeof a === 'object' ? a.id : a));
        params.set('assignee', ids.join(','));
    }

    if (filterState.tags?.length) {
        const names = filterState.tags.map((t) => (typeof t === 'object' ? t.name : t));
        params.set('tags', names.join(','));
    }

    if (filterState.referral) params.set('referral', filterState.referral);
    if (filterState.startDate) params.set('startDate', filterState.startDate);
    if (filterState.endDate) params.set('endDate', filterState.endDate);
    if (filterState.depositPaid) params.set('depositPaid', filterState.depositPaid);
    if (filterState.archiveReason) params.set('archiveReason', filterState.archiveReason);

    if (filterState.bookedWith?.length) {
        const ids = filterState.bookedWith.map((b) => (typeof b === 'object' ? b.id : b));
        params.set('bookedWith', ids.join(','));
    }

    if (filterState.consultationBookedWith?.length) {
        const ids = filterState.consultationBookedWith.map((c) => (typeof c === 'object' ? c.id : c));
        params.set('consultationBookedWith', ids.join(','));
    }

    if (filterState.noTreatment) params.set('noTreatment', 'true');

    return params;
};

/**
 * Hook that syncs table filter state bidirectionally with URL search params.
 *
 * @returns {{ initialFilters: object, syncToUrl: function, hasUrlFilters: boolean }}
 */
const useTableQueryParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialFilters = useMemo(() => parseFiltersFromUrl(searchParams), []);

    const hasUrlFilters = useMemo(() => Object.keys(initialFilters).length > 0, [initialFilters]);

    const syncToUrl = useCallback((filterState) => {
        if (!filterState || Object.keys(filterState).length === 0) {
            setSearchParams({}, { replace: true });
            return;
        }
        const params = serializeFiltersToUrl(filterState);
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    return { initialFilters, syncToUrl, hasUrlFilters };
};

export default useTableQueryParams;
